// ** React Imports
import { useState, useEffect, useRef } from "react";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

// ** Custom Components
import { ButtonSecondary } from "components/layout/Button/ButtonSecondary";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardBody } from "components/layout/Card/CardBody";

// ** User Components
import DescriptionInfoMobile from "./DescriptionInfoMobile";
import SearchInfo from "./SearchInfo";
import PreferencesInfo from "./PreferencesInfo";
import Links from "./Links";
import DataInfo from "./DataInfo";

// ** Hooks
import { useMemoizedUser } from "hooks/useMeMoizedUser";

// #####################################################

const DynamicTextContainer = styled.div`
	overflow: hidden;
	transition: height 0.15s ease;
`;

const StyledDynamicText = styled.div`
	word-break: break-word;
	${({ $short }) =>
		$short &&
		css`
			overflow: hidden;
			text-overflow: ellipsis;
			max-height: 150px;
		`}
`;

const StyledGradientDivider = styled.div`
	${({ $withGradient, theme: { isDarkMode } }) =>
		$withGradient &&
		css`
			position: absolute;
			top: -40px;
			height: 40px;
			width: 100%;
			background: ${isDarkMode
				? "linear-gradient(180deg, rgba(32, 32, 32, 0) 0%, rgba(32, 32, 32, 1) 90%, rgba(32, 32, 32, 1) 100%)"
				: "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%,  rgba(255, 255, 255, 1) 90%, rgba(255, 255, 255, 1) 100%)"};
		`}
`;

// #####################################################

const UserProfileInfoMobile = () => {
	const { t } = useTranslation(["userProfile", "common"]);
	const user = useMemoizedUser();

	const isSearchVisible =
		user?.searchSexArray?.length !== 0 ||
		user?.searchAgeObject?.from !== null ||
		user?.searchAgeObject?.to !== null ||
		user?.searchForArray?.length !== 0;

	const isPreferencesVisible =
		user?.preferencesArray?.length !== 0 ||
		user?.interestArray?.length !== 0 ||
		user?.tastesArray?.length !== 0;

	const [expanded, setExpanded] = useState(false);
	const [isContentOverflowing, setIsContentOverflowing] = useState(false);

	const contentRef = useRef(null);
	const shortTextRef = useRef(null);
	const shortTextHeight = useRef(0);

	useEffect(() => {
		if (shortTextRef.current) {
			shortTextHeight.current = shortTextRef.current.clientHeight;
		}
	}, []);

	useEffect(() => {
		if (
			contentRef.current &&
			shortTextRef.current &&
			shortTextHeight.current > 0
		) {
			const height = contentRef.current.scrollHeight;
			contentRef.current.style.height = expanded
				? `${height}px`
				: `${shortTextHeight.current}px`;
		}
	}, [expanded, shortTextHeight.current]);

	useEffect(() => {
		const checkOverflow = () => {
			if (
				contentRef.current &&
				shortTextRef.current &&
				shortTextRef.current.scrollHeight > 150
			) {
				setIsContentOverflowing(true);
			}
			if (
				contentRef.current &&
				shortTextRef.current &&
				shortTextRef.current.scrollHeight <= 150
			) {
				setIsContentOverflowing(false);
			}
		};
		checkOverflow();
		window.addEventListener("resize", checkOverflow);

		return () => {
			window.removeEventListener("resize", checkOverflow);
		};
	}, [expanded]);

	return (
		<CardWrapper>
			<CardBody>
				<DynamicTextContainer ref={contentRef}>
					<StyledDynamicText ref={shortTextRef} $short={!expanded}>
						<DescriptionInfoMobile user={user} />
						<Links user={user} />
						{isSearchVisible ? <SearchInfo user={user} /> : null}
						{isPreferencesVisible ? (
							<PreferencesInfo
								user={user}
								isSearchVisible={isSearchVisible}
							/>
						) : null}
						<DataInfo
							user={user}
							isSearchVisible={
								isPreferencesVisible || isSearchVisible
							}
						/>
					</StyledDynamicText>
				</DynamicTextContainer>
				{isContentOverflowing && (
					<>
						<div style={{ position: "relative" }}>
							<StyledGradientDivider $withGradient={!expanded} />
						</div>
						<ButtonSecondary
							activeOnlyCaret={expanded}
							label={
								expanded
									? t("common:showLess")
									: t("common:showMore")
							}
							onClick={() => {
								setExpanded((prev) => !prev);
							}}
							block
							dropdown
							mt={expanded ? 2 : 0}
						/>
					</>
				)}
			</CardBody>
		</CardWrapper>
	);
};

// #####################################################

export default UserProfileInfoMobile;

// #####################################################
